// Camera `orientation` field: degrees to rotate the raw frame so it displays
// upright (90/270 = camera physically mounted vertical, in either rotation
// direction; 180 = mounted horizontal but upside down; 360 = normal horizontal).
// Older docs may still hold the retired 'vertical'/'horizontal' strings — those
// are treated as unconfigured (LEGACY_FALLBACK) until re-saved with a degree value.
export const ORIENTATION_DEGREES = ['90', '180', '270', '360'];

export const ORIENTATION_LABELS = {
  '360': '360° (horizontal)',
  '180': '180° (horizontal invertido)',
  '90': '90° (vertical - sentido horario)',
  '270': '270° (vertical - sentido antihorario)',
};

// null means "no valid degree configured" — caller should fall back to
// legacy auto-detection instead of rotating.
export const getRotationDegrees = (orientation) =>
  ORIENTATION_DEGREES.includes(orientation) ? Number(orientation) : null;

// Computes how to display a camera's preview image inside its card:
// - 90/270: the raw frame is landscape but the real content is portrait, so
//   the image is scaled to its rotated bounding box and rotated in place,
//   leaving the container's background visible as bars on the sides.
// - 180/0: rotation that doesn't change the bounding box shape, so a plain
//   cover + rotate is enough.
// - unconfigured (rotationDeg === null): legacy behavior — guess from pixel
//   dimensions, never rotate (we don't know the mounting direction).
// `visibleBounds` (leftPercent/widthPercent, horizontal, % of the container)
// says where the actual image content sits when it's letterboxed — i.e.
// everywhere outside that range is the black-bar background, not the image.
// Callers with a horizontal drag/seek interaction (the before/after slider)
// need this to keep the interactive range over the image itself.
const FULL_BOUNDS = { leftPercent: 0, widthPercent: 100 };

export function computeOrientedImageStyle({ rotationDeg, naturalWidth, naturalHeight, containerWidth, containerHeight }) {
  if (rotationDeg === 90 || rotationDeg === 270) {
    const scale = Math.min(containerWidth / naturalHeight, containerHeight / naturalWidth);
    const width = naturalWidth * scale;
    const height = naturalHeight * scale;
    // Post-rotation the bounding box swaps: visual width is the pre-rotation height.
    const widthPercent = containerWidth ? Math.min(100, (height / containerWidth) * 100) : 100;
    return {
      isVertical: true,
      visibleBounds: { leftPercent: (100 - widthPercent) / 2, widthPercent },
      style: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: `${width}px`,
        height: `${height}px`,
        transform: `translate(-50%, -50%) rotate(${rotationDeg}deg)`,
        objectFit: 'contain',
      },
    };
  }

  if (rotationDeg === 180 || rotationDeg === 0) {
    return {
      isVertical: false,
      visibleBounds: FULL_BOUNDS,
      style: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transform: rotationDeg ? `rotate(${rotationDeg}deg)` : undefined,
      },
    };
  }

  const isVertical = naturalHeight > naturalWidth;
  let visibleBounds = FULL_BOUNDS;
  if (isVertical && containerWidth && containerHeight) {
    const scale = Math.min(containerWidth / naturalWidth, containerHeight / naturalHeight);
    const widthPercent = Math.min(100, ((naturalWidth * scale) / containerWidth) * 100);
    visibleBounds = { leftPercent: (100 - widthPercent) / 2, widthPercent };
  }
  return {
    isVertical,
    visibleBounds,
    style: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: isVertical ? 'contain' : 'cover',
    },
  };
}

// A before/after slider clips the "before" image with inset(top right bottom
// left) to reveal it left-to-right on screen as sliderPos grows. That inset
// box is defined in the element's own (pre-rotation) coordinates, so once
// `rotate(Ndeg)` is applied the clipped edge moves with it — verified
// empirically: 90deg maps pre-rotation top/bottom to screen right/left, 270deg
// maps them the other way, and 180deg maps pre-rotation left/right to screen
// right/left. Each case below clips the edge that keeps the reveal
// left-to-right on screen after that rotation.
export function getBeforeImageClipPath(rotationDeg, sliderPos) {
  const hidden = 100 - sliderPos;
  if (rotationDeg === 90) return `inset(${hidden}% 0 0 0)`;
  if (rotationDeg === 270) return `inset(0 0 ${hidden}% 0)`;
  if (rotationDeg === 180) return `inset(0 0 0 ${hidden}%)`;
  return `inset(0 ${hidden}% 0 0)`;
}
